'use client';

import { CommandGroup } from '@/components/ui/command';
import { useMatter, useMatters } from '@/lib/services/matters/hooks';
import { useRecentMatterIds } from '@/lib/hooks/use-recent-matters';
import {
  transformMatterToSearchResult,
  type MatterSearchResult
} from '@/components/search/hooks/use-search';
import { MatterResultItem } from './search-results-view';
import type { Matter } from '@/lib/services/matters/types';

const RECENT_MATTERS_LIMIT = 3;

/**
 * RecentMattersView Component
 *
 * Renders this user's genuinely recent matters — ones they've viewed or
 * selected from search, tracked client-side via `useRecentMatterIds`
 * (LF-4960) — as clickable rows in the blank/initial (`list`) state of the
 * search modal, so the palette is useful the moment it opens.
 *
 * Before this fix the section showed the firm's most-recently-*updated*
 * matters (anyone's edits), which had nothing to do with this user's own
 * activity. That firm-wide list is kept as a fallback only for a fresh
 * browser with no tracked history yet, so the section is never an empty
 * void on first open.
 *
 * Reuses the search-result row (`MatterResultItem`) so the row layout
 * matches the typed-search results exactly. Degrades gracefully: renders
 * nothing while loading, on error, or with no matters at all.
 */
export function RecentMattersView({
  onSelect
}: {
  onSelect: (matter: MatterSearchResult) => void;
}) {
  const trackedIds = useRecentMatterIds(RECENT_MATTERS_LIMIT);

  // Rules of Hooks require a fixed call count; RECENT_MATTERS_LIMIT is a
  // constant, so three fixed slots cover it. `useMatter` no-ops (disabled)
  // when passed a falsy id.
  const slot0 = useMatter(trackedIds[0] ?? 0);
  const slot1 = useMatter(trackedIds[1] ?? 0);
  const slot2 = useMatter(trackedIds[2] ?? 0);

  const trackedById = new Map<number, Matter>();
  for (const slot of [slot0, slot1, slot2]) {
    if (slot.data) trackedById.set(slot.data.id, slot.data);
  }
  const tracked = trackedIds
    .map((id) => trackedById.get(id))
    .filter((m): m is Matter => !!m)
    .map(transformMatterToSearchResult);

  const { data: fallbackData } = useMatters(
    {
      sortBy: 'updatedAt',
      sortOrder: 'DESC',
      includeArchived: 'false',
      limit: RECENT_MATTERS_LIMIT
    },
    { enabled: trackedIds.length === 0 }
  );

  const recent =
    tracked.length > 0
      ? tracked
      : (fallbackData?.matters ?? [])
          .slice(0, RECENT_MATTERS_LIMIT)
          .map(transformMatterToSearchResult);

  if (recent.length === 0) return null;

  return (
    <CommandGroup heading="Recent">
      {recent.map((matter) => (
        <MatterResultItem key={matter.id} matter={matter} onSelect={onSelect} />
      ))}
    </CommandGroup>
  );
}
